import { ArrowPathIcon, CheckIcon, EnvelopeIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiRequest } from "src/async/apiUtils";
import { useAsyncAction } from "src/hooks/useAsyncAction";

const Card = ({ children }) => (
  <div className="relative overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-slate-200">
    <div className="h-1.5 w-full bg-gray-300" />
    <div className="p-8 md:p-10">{children}</div>
  </div>
);

const Header = ({ title, subtitle }) => (
  <div className="flex items-start gap-4">
    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-200">
      <EnvelopeIcon className="h-7 w-7" />
    </div>
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">{title}</h1>
      {subtitle ? <p className="mt-1 text-slate-600">{subtitle}</p> : null}
    </div>
  </div>
);

export default function Unsubscribe() {
  const { token } = useParams();

  const [verifyState, setVerifyState] = useState("verifying");
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({ reason: "" });
  const [unsubscribed, setUnsubscribed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!token) {
          setVerifyState("invalid");
          setError("Missing unsubscribe token.");
          return;
        }

        await apiRequest("GET", `portal/email/unsubscribe/token/verify`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setVerifyState("ready");
      } catch (e) {
        setVerifyState("invalid");
        setError(e?.message || "Invalid or expired unsubscribe link.");
      }
    })();
  }, [token]);

  const { loading: submitting, execute: unSubscribeEmail } = useAsyncAction({
    onSuccess: () => setUnsubscribed(true),
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    unSubscribeEmail(async () => {
      return await apiRequest(
        "POST",
        "portal/email/unsubscribe",
        {
          body: { reason: formData.reason?.trim() || undefined },
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    });
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4">
      <div className="w-full max-w-2xl">
        {verifyState === "verifying" && (
          <Card>
            <Header
              title="Checking your link…"
              subtitle="Hold on while we verify your unsubscribe token."
            />
            <div className="mt-6 flex items-center gap-3 text-slate-600">
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
              <span>Verifying…</span>
            </div>
          </Card>
        )}

        {verifyState === "invalid" && (
          <Card>
            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-rose-50 p-3 ring-1 ring-rose-200">
                <ExclamationTriangleIcon className="h-7 w-7 text-rose-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">This link can’t be used</h1>
                <p className="mt-1 text-slate-600">{error || "The unsubscribe token is invalid or has expired. You can request a new email or contact support."}</p>
              </div>
            </div>

            <div className="mt-6">
              <a
                href="/"
                className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-5 py-3 text-white shadow-sm hover:bg-slate-800">
                Go home
              </a>
            </div>
          </Card>
        )}

        {verifyState === "ready" && unsubscribed && (
          <Card>
            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-green-50 p-3 ring-1 ring-green-200">
                <CheckIcon className="h-7 w-7 text-green-700" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">You’ve been unsubscribed</h1>
                <p className="mt-1 text-slate-600">We’ve processed your request. You may still receive messages already scheduled, but that should stop soon.</p>
              </div>
            </div>

            <p className="mt-6 text-center text-xs leading-relaxed text-slate-500">In case if change your mind? Contact the administrator.</p>
          </Card>
        )}

        {verifyState === "ready" && !unsubscribed && (
          <Card>
            <Header
              title="Unsubscribe from emails"
              subtitle="Confirm you'd like to stop receiving messages. You can optionally tell us why—this helps us improve."
            />

            <form
              className="mt-6 space-y-3"
              onSubmit={handleSubmit}>
              <div className="mt-2 grid gap-2">
                <label
                  htmlFor="other"
                  className="text-sm text-slate-600">
                  Why are you unsubscribing? <span className="text-slate-400">(optional)</span>
                </label>
                <textarea
                  id="other"
                  rows={4}
                  value={formData.reason}
                  onChange={(e) => setFormData((prev) => ({ ...prev, reason: e.target.value }))}
                  placeholder="Share feedback to help us improve (optional)"
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100"
                />
              </div>

              {error ? <p className="text-sm text-rose-600">{error}</p> : null}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60">
                  {submitting ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      <span>Processing…</span>
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      <span>Confirm Unsubscribe</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
